import React, { useState, useEffect, useRef, useMemo } from 'react';
import { message as antMessage, Spin, Button, Badge } from 'antd';
// Import all your existing components
import MasterDetailTable from './MasterDetailTable';
import MarkdownText from './MarkdownText';
import ListDetailTables from './ListDetailTables';
import DecisionTree from './DecisionTree';
import StepsComponent from './StepsComponent';
import BarChartComponent from './BarChartComponent';
import LineChartComponent from './LineChartComponent';
import PieChartComponent from './PieChartComponent';
import BubbleChartComponent from './BubbleChartComponent';
import ScatterChartComponent from './ScatterChartComponent';
import SearchResults from './SearchResults';
import AnnotatedDocument from './AnnotatedDocument';
import ListComponent from './ListComponent';
import ChatComponent from './ChatComponent';
import ActionCenter from './ActionCenter';
import HeaderActionCenter from './HeaderActionCenter';
import InputSelectableList from './InputSelectableList';
import InputEditableList from './InputEditableList';
import SimpleTableDisplay from './SimpleTableDisplay';
import AccordionTableDisplay from './AccordionTableDisplay';
import FinOpsTableComponent from './FinOpsTableComponent';

/**
 * Dynamic Component Manager with automated display and version tracking
 */
const DraggableLayoutManager = ({ 
  apiData, 
  layout, 
  onVersionChange,
  onChatMessage, 
  sessionId,
  activeAgent,
  currentWorkflowState,
  username = 'testuser',
  onComponentUpdate,
  projectId,
  progressData,
  isProcessing,
  supportsProgressTracking  
}) => {
  //console.log("DraggableLayoutManager rendering with apiData:", apiData);
  //console.log("Layout configuration:", layout);
  
  // State for tracking components and their active versions
  const [componentVersions, setComponentVersions] = useState({});
  const [componentDisplay, setComponentDisplay] = useState({});
  const [componentsToDisplay, setComponentsToDisplay] = useState([]);
  
  // State for tracking component registry for updates
  const [componentRegistry, setComponentRegistry] = useState({});
  const [allComponentIds, setAllComponentIds] = useState([]);
  const [componentTypes, setComponentTypes] = useState({});
  
  // State for resizable columns
  const [leftColumnWidth, setLeftColumnWidth] = useState(50); // Default 50%
  
  // Remember the components registered already
  const registeredComponentsRef = useRef(new Set());

  // Track the previous apiData for comparison
  const prevApiDataRef = useRef(null);
  
  const [chatLoading, setChatLoading] = useState(false);

  const [evaluationResult, setEvaluationResult] = useState(null);


  // Determine column count based on agent configuration or layout prop
  const columnCount = useMemo(() => {
    // First check if layout is a direct number
    if (typeof layout === 'number') {
      if (layout === 1) return 1;  // Add support for 1
      return layout === 3 ? 3 : 2;
    }
    
    // Next check if activeAgent has layout_columns
    if (activeAgent && activeAgent.layout_columns) {
      if (activeAgent.layout_columns === 1) return 1;  // Add support for 1
      return activeAgent.layout_columns === 3 ? 3 : 2;
    }
    
    // Default to 2 columns
    return 2;
  }, [layout, activeAgent]);


   ////////////////// progres tracking method //////////////////



   
const renderLoadingState = () => {
  // Check if we're still waiting for initial data load
  console.log('=== renderLoadingState Debug ===');
  console.log('apiData:', !!apiData);
  console.log('isProcessing:', isProcessing);
  console.log('supportsProgressTracking:', supportsProgressTracking);
  console.log('progressData:', progressData);
  console.log('activeAgent?.progress_tracking:', activeAgent?.progress_tracking);


  if (!apiData && !isProcessing) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading components...</div>
      </div>
    );
  }

  // Check if we're processing (this covers both initial load and ongoing processing)
  if (isProcessing || (!apiData && supportsProgressTracking)) {
    if (supportsProgressTracking && progressData) {
      // Show detailed progress for agents that support it
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '24px' }}>
            <h3>Processing {activeAgent?.name || 'Agent'}</h3>
            <div style={{ marginBottom: '16px' }}>
              <strong>{progressData.step_name}</strong>
            </div>
            <div style={{ 
              width: '100%', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '10px',
              marginBottom: '8px'
            }}>
              <div style={{
                width: `${progressData.percentage}%`,
                backgroundColor: '#6200EE',
                height: '8px',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Step {progressData.current_step} of {progressData.total_steps} ({progressData.percentage}%)
            </div>
          </div>
        </div>
      );
    } else {
      // Show simple loading for agents without progress tracking or while waiting for progress data
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '24px' }}>
            <h3>Processing {activeAgent?.name || 'Agent'}</h3>
            <div style={{ fontSize: '16px', color: '#666', marginTop: '12px' }}>
              {supportsProgressTracking ? 'Initializing...' : 'Loading data...'}
            </div>
          </div>
        </div>
      );
    }
  }

  return null;
};

  ////////////////////////////////////////

  


  // Process API data when it changes
  useEffect(() => {
    if (!apiData) return;
    
    console.log("Processing updated apiData:", Object.keys(apiData));
    
    try {
      // Extract component IDs and types
      const componentIds = [];
      const typesMap = {};
      const currentDisplay = { ...componentDisplay };
      
      // Process each component in the apiData
      Object.entries(apiData).forEach(([key, componentData]) => {
        // Check if this is actually a component (has uuid and type properties)
        if (componentData && typeof componentData === 'object' && 
            componentData.uuid && componentData.type) {
          componentIds.push(componentData.uuid);
          typesMap[componentData.uuid] = {
            type: componentData.type,
            title: key
          };
        }
      });
      
      // Update state
      setAllComponentIds(componentIds);
      setComponentTypes(typesMap);
      
      // Update the previous data reference
      prevApiDataRef.current = apiData;
    } catch (error) {
      console.error("Error processing components:", error);
    }
  }, [apiData]);





  // Update display settings for new components
  useEffect(() => {
    if (!apiData) return;
    
    const updatedSettings = {...componentDisplay};
    let hasChanges = false;
    
    Object.entries(apiData).forEach(([key, componentData]) => {
      // Skip chat components
      if (key === "Agent Conversation" || 
          (componentData && componentData.type === "chat_component")) {
        return;
      }
      
      // Set all non-chat components to visible by default if not already set
      if (updatedSettings[key] === undefined) {
        updatedSettings[key] = true;
        hasChanges = true;
      }
    });
    
    // Only update state if needed to avoid loops
    if (hasChanges) {
      setComponentDisplay(updatedSettings);
    }
  }, [apiData]);
  

  
  // Update the list of components to display whenever display preferences change
  useEffect(() => {
    if (apiData) {
      // Create a stringified version of what would be displayed
      const newComponentsToDisplay = Object.entries(apiData)
        .filter(([key, comp]) => {
          return key !== "Agent Conversation" && 
                comp && 
                typeof comp === 'object' &&
                comp?.type !== "chat_component" &&
                comp?.uuid &&  // Must have uuid
                comp?.type &&  // Must have type
                comp?.data &&  // Must have data
                componentDisplay[key];
        })
        .map(([key]) => key);
        
      // Compare with current displayed components
      const currentDisplayed = componentsToDisplay.map(c => c);
      
      // Only update if the list of keys has changed
      if (JSON.stringify(newComponentsToDisplay) !== JSON.stringify(currentDisplayed)) {
        setComponentsToDisplay(newComponentsToDisplay);
      }
    }
  }, [componentDisplay, apiData]);




  // Register a component with the registry
  const registerComponent = (componentId, refreshCallback) => {
    if (!componentId || !refreshCallback) return () => {};
    
    // Only register if this component hasn't been registered yet in this render cycle
    if (!registeredComponentsRef.current.has(componentId)) {
      console.log(`Registering component: ${componentId}`);
      registeredComponentsRef.current.add(componentId);
      
      setComponentRegistry(prev => ({
        ...prev,
        [componentId]: refreshCallback
      }));
    } else {
      console.log(`Component ${componentId} already registered, skipping`);
    }
    
    // Return an unregister function
    return () => {
      registeredComponentsRef.current.delete(componentId);
      setComponentRegistry(prev => {
        const newRegistry = { ...prev };
        delete newRegistry[componentId];
        return newRegistry;
      });
    };
  };

  // Notify specific components to update
  const notifyComponentUpdates = (componentIds) => {
    if (!componentIds || !Array.isArray(componentIds)) return;
    
    console.log("Notifying components to update:", componentIds);
    componentIds.forEach(id => {
      if (componentRegistry[id]) {
        componentRegistry[id](); // Call the refresh callback
      } else {
        console.log(`Component not found in registry: ${id}`);
      }
    });
  };
  
  // Improved handleComponentClick function that always fetches the specific version
  const handleComponentClick = (componentRef) => {
    if (!componentRef) return;
    
    console.log("Component click handler:", componentRef);
    
    try {
      // Get the title or key for the component
      const title = componentRef.title || componentRef.componentKey;
      const uuid = componentRef.uuid;
      const requestedVersion = componentRef.version || 1;
      
      if (!uuid) {
        console.warn("Component reference missing UUID");
        antMessage.error("Invalid component reference");
        return;
      }
      
      // Always fetch specific version from backend
      console.log(`Fetching component: ${uuid}, version: ${requestedVersion} from backend`);
      if (onVersionChange) {
        antMessage.info(`Loading ${title} (version ${requestedVersion})...`);
        onVersionChange(uuid, requestedVersion);
      } else {
        antMessage.error(`Cannot load component: ${title}`);
      }
    } catch (error) {
      console.error("Error handling component click:", error);
      antMessage.error("Failed to show component");
    }
  };

  // Use React's useMemo to memoize components
  const memoizedCreateComponent = useMemo(() => {
    const componentCache = {};
    
    return (key, componentData, onVersionChange) => {
      const cacheKey = `${key}-${componentData?.uuid}-${componentData?.current_version}`;
      
      // Return cached component if it exists and data hasn't changed
      if (componentCache[cacheKey] && 
          JSON.stringify(componentCache[cacheKey].data) === JSON.stringify(componentData?.data)) {
        return componentCache[cacheKey].component;
      }
      
      // Otherwise create the component
      const component = createComponent(key, componentData, onVersionChange);
      
      // Cache it
      componentCache[cacheKey] = {
        component,
        data: componentData?.data
      };
      
      return component;
    };
  }, []);

  // Create a component based on its type
  const createComponent = (key, componentData, onVersionChange) => {
    console.log(`Creating component for key: ${key}`, componentData);

    if (!componentData) {
      console.error(`Missing component data for key: ${key}`);
      return null;
    }
    
    console.log(`Creating real component for ${key}:`, componentData.type);
    
    // Set up props for the component
    const props = {
      data: componentData.data,
      title: key,
      uuid: componentData.uuid || '',
      currentVersion: componentData.current_version || 1,
      versionInfo: componentData.version_info || {},
      onVersionChange: onVersionChange,
      registerComponent: componentData.uuid ? (callback) => registerComponent(componentData.uuid, callback) : null
    };
    
    // Create component based on type
    try {
      if (componentData.type === 'master_detail_table') {
        return <MasterDetailTable {...props} />;
      } else if (componentData.type === 'markdown_text') {
        return <MarkdownText {...props} />;
      } else if (componentData.type === 'list_detail_tables') {
        return <ListDetailTables {...props} />;
      } else if (componentData.type === 'decision_tree') {
        return <DecisionTree {...props} />;  
      } else if (componentData.type === 'process_steps') {
        return <StepsComponent {...props} />;
      } else if (componentData.type === 'bar_chart') {
        return <BarChartComponent {...props} />;
      } else if (componentData.type === 'line_chart') {
        return <LineChartComponent {...props} />;
      } else if (componentData.type === 'pie_chart') {
        return <PieChartComponent {...props} />;
      } else if (componentData.type === 'bubble_chart') {
        return <BubbleChartComponent {...props} />;
      } else if (componentData.type === 'scatter_chart') {
        return <ScatterChartComponent {...props} />;
      } else if (componentData.type === 'search_results') {
        return <SearchResults {...props} />;
      } else if (componentData.type === 'finops_table') {
        return <FinOpsTableComponent {...props} />;
      } else if (componentData.type === 'annotated_document') {
        return <AnnotatedDocument {...props} />;
      } else if (componentData.type === 'list_component') {
        return <ListComponent {...props} />;
      } else if (componentData.type === 'chat_component') {
        return <ChatComponent 
          {...props} 
          isLoading={chatLoading}
          onSendMessage={(message, chatUuid) => handleChatMessage(message, chatUuid, notifyComponentUpdates, currentWorkflowState?.agent_key)}
          onFileUpload={handleFileUpload}
          onComponentClick={handleComponentClick}
          sessionId={sessionId}
          agent_key={currentWorkflowState?.agent_key}
          project_id={props.projectId}
          component_map={currentWorkflowState?.component_map}
        />;
      } else if (componentData.type === 'input_selectable_list') {
        return <InputSelectableList 
          {...props}
          sessionId={sessionId}
          endpointUrl={`${activeAgent?.endpoint || ''}/api/list/selectable`}
          onComponentUpdate={onComponentUpdate} // Pass the handler here
          project_id={props.projectId}
          agent_key={currentWorkflowState?.agent_key}
        />;
      } else if (componentData.type === 'input_editable_list') {
        return <InputEditableList
          {...props}
          sessionId={sessionId}
          endpointUrl={`${activeAgent?.endpoint || ''}/api/list/editable`}
          project_id={props.projectId}
        />;
      } else if (componentData.type === 'simple_table_display') {
        return <SimpleTableDisplay
          {...props}
          sessionId={sessionId}
          project_id={props.projectId}
          agent_key={currentWorkflowState?.agent_key}
        />;
      } else if (componentData.type === 'action_center') {
        return <ActionCenter 
          {...props} 
          activeAgent={activeAgent}
          componentIds={allComponentIds}
          componentTypes={componentTypes}
          sessionId={sessionId}
          onActionComplete={(action, data) => {
            if (data && data.updatedComponentIds) {
              notifyComponentUpdates(data.updatedComponentIds);
            }
          }}
          project_id={props.projectId}
        />;
      } else if (componentData.type === 'accordion_table_display') {
        console.log('Props object:', props);
        console.log('projectId:', projectId);
        console.log('Props.projectId:', props.projectId);
        return <AccordionTableDisplay
          {...props}
          sessionId={sessionId}
          project_id={projectId}
          agent_key={currentWorkflowState?.agent_key}
        />;
      } else {
        console.error('Unknown component type:', componentData.type);
        return null;
      }
    } catch (error) {
      console.error('Error creating component:', error);
      return null;
    }
  };
  
  // Create a wrapper for the onChatMessage function
  const handleChatMessage = async (message, chatUuid, notifyComponentRefresh, agentKey, projectId) => {
    try {
      // Set loading state
      setChatLoading(true);
      
      // Call the original onChatMessage prop
      const success = await onChatMessage(message, chatUuid, notifyComponentRefresh, agentKey, projectId);
      
      return success;
    } catch (error) {
      console.error("Error processing chat message:", error);
      antMessage.error(`Error: ${error.message}`);
      return false;
    } finally {
      // Always clear loading state after a short delay to ensure UI transitions smoothly
      setTimeout(() => {
        setChatLoading(false);
      }, 300);
    }
  };

  // Create a synthetic component for the given type
  const createGeneratedComponent = (key, type) => {
    console.log(`Missing component data for key: ${key}, type: ${type}`);
    // Return null instead of synthetic component
    return null;
  };
  



  const handleComponentUpdate = (result) => {
    if (!result) {
      console.warn('Empty result received in handleComponentUpdate');
      return;
    }
    
    console.log('Component update result received:', result);
    
    // Update evaluation result state if available
    if (result.evaluationResult !== undefined) {
      // If you have evaluationResult state
      if (typeof setEvaluationResult === 'function') {
        setEvaluationResult(result.evaluationResult);
      }
    }
    
    // Instead of directly updating apiData, pass the result to the parent component
    if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
      console.log(`Components updated: ${result.updatedComponentIds.join(', ')}`);
      // If notifyComponentUpdates is defined, use it
      if (typeof notifyComponentUpdates === 'function') {
        notifyComponentUpdates(result.updatedComponentIds);
      }
    }
    
    // If onComponentUpdate prop is provided, use it
    if (onComponentUpdate && typeof onComponentUpdate === 'function') {
      onComponentUpdate(result);
    }
  };  




  const handleFileUpload = async (files, chatUuid, context, sessionId) => {
    try {
      console.log("Uploading files for context:", context);
      
      // Check if we have API endpoint information from the context
      const endpoint = context.endpoint || `${activeAgent?.endpoint}/upload`;
      
      // Create form data
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Add context information
      formData.append('chatUuid', chatUuid);
      if (context.documentType) formData.append('documentType', context.documentType);
      if (context.requestId) formData.append('requestId', context.requestId);
      

      console.log('==========  ',  context, currentWorkflowState.agent_key)


      // Add metadata if available
      if (context.metadata) {
        formData.append('metadata', JSON.stringify(context.metadata));
      }
      // Add metadata if available
      if (context.fileMetadata) {
        formData.append('fileMetadata', JSON.stringify(context.fileMetadata));
      }      
      
      formData.append('context', JSON.stringify(context));



      // Add agent_key if available
      if (currentWorkflowState?.agent_key) {
        formData.append('agent_key', currentWorkflowState.agent_key);
      }
      
      // Add component_map if available
      if (context.component_map || currentWorkflowState?.component_map) {
        const componentMap = context.component_map || currentWorkflowState?.component_map;
        formData.append('component_map', JSON.stringify(componentMap));
      }

      console.log('==========>>  ',  formData)


      // Call the API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        body: formData,
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }
      
      // Parse the response
      const result = await response.json();
      
      // If the response includes updatedComponentIds or componentIds, notify those components
      if (result.updatedComponentIds && result.updatedComponentIds.length > 0) {
        notifyComponentUpdates(result.updatedComponentIds);
      }
      
      // Update the apiData state with any new components from the response
      if (result) {
        handleComponentUpdate(result);
      }
      
      antMessage.success(`${files.length} file(s) uploaded successfully`);
      return true;
    } catch (error) {
      console.error("Error uploading files:", error);
      antMessage.error("Failed to upload files");
      return false;
    }
  };

  /*
  // Handle file uploads
  const handleFileUpload = async (files, chatUuid) => {
    try {
      console.log("Uploading files:", files);
      await new Promise(resolve => setTimeout(resolve, 1000));
      antMessage.success(`${files.length} file(s) uploaded successfully`);
      return true;
    } catch (error) {
      console.error("Error uploading files:", error);
      antMessage.error("Failed to upload files");
      return false;
    }
  };
  */




  // Handle resize for the splitter
  const handleResize = (newPercentage) => {
    // Constrain the percentage between 30% and 70%
    const constrainedWidth = Math.min(Math.max(newPercentage, 30), 70);
    setLeftColumnWidth(constrainedWidth);
  };



  // Find chat component in apiData
  const getChatComponent = () => {
    if (!apiData) return null;
    
    // Look for the chat component in apiData
    const chatEntry = Object.entries(apiData).find(([key, data]) => 
      (key === "Agent Conversation" || 
      (data && typeof data === 'object' && data.type === "chat_component")) &&
      data.uuid && data.data  // Ensure it has the required structure
    );

    if (chatEntry) {
      const [key, componentData] = chatEntry;
      return memoizedCreateComponent(key, componentData, onVersionChange);
    }
    
    return null;
  };
  


  // Toggle component display
  const toggleComponent = (key) => {
    setComponentDisplay(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  /*
  // If no apiData, show loading
  if (!apiData) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading components...</div>
      </div>
    );
  }
    */

    // Use the enhanced loading state
    if (!apiData) {
      return renderLoadingState();
    }

  // Get the chat component
  const chatComponent = getChatComponent();

  // Get keys of components from apiData (excluding chat)
  //const componentKeys = Object.keys(apiData).filter(key => 
  //  key !== "Agent Conversation" && 
  //  apiData[key] && 
  //  apiData[key].type !== "chat_component"
  //);
  const componentKeys = Object.keys(apiData).filter(key => {
  const item = apiData[key];
  return key !== "Agent Conversation" && 
         item && 
         typeof item === 'object' &&
         item.type !== "chat_component" &&
         item.uuid && // Must have uuid to be a component
         item.type && // Must have type to be a component
         item.data;   // Must have data to be a component
});

  // Calculate column widths
  const rightColumnWidth = 100 - leftColumnWidth;

  // If we have a 3-column layout, split the right column in half
  // This is a simplified approach to start with
  const col2Width = columnCount === 3 ? rightColumnWidth / 2 : rightColumnWidth;
  const col3Width = columnCount === 3 ? rightColumnWidth / 2 : 0;

  console.log('column count ', columnCount)
  
  // Get components for each column (with 1-column support)
  const getComponentsForColumn = (columnIndex) => {
    // Special handling for 1-column layout
    if (columnCount === 1 && columnIndex === 1) {
      // In 1-column layout, column 1 has both chat and all other components
      const components = [];
      
      // Add chat component first if it exists
      if (chatComponent) {
        components.push(
          <div key="chat" style={{ marginBottom: '16px' }}>
            {chatComponent}
          </div>
        );
      }
      
      // Add all other components
      componentsToDisplay
        .filter(key => apiData[key] && apiData[key].data)
        .forEach(key => {
          components.push(
            <div key={key} style={{ marginBottom: '16px', position: 'relative' }}>
              {memoizedCreateComponent(key, apiData[key], onVersionChange)}
              <div style={{ 
                position: 'absolute', 
                top: '4px', 
                right: '4px',
                zIndex: 1000
              }}>
                <Button 
                  size="small" 
                  shape="circle" 
                  type="text"
                  onClick={() => toggleComponent(key)}
                  title="Hide component"
                >
                  ✕
                </Button>
              </div>
            </div>
          );
        });
      
      return components;
    }
    else {
      // Original logic for 2 and 3 column layouts
      if (columnIndex === 1) {
        // Column 1 always has just the chat component
        return chatComponent ? [chatComponent] : [];
      }
      
      if (columnCount === 2 && columnIndex === 2) {
        // In a 2-column layout, column 2 has all non-chat components
        return componentsToDisplay
          .filter(key => apiData[key] && apiData[key].data) // Only include components with actual data
          .map(key => (
            <div key={key} style={{ marginBottom: '16px', position: 'relative' }}>
              {memoizedCreateComponent(key, apiData[key], onVersionChange)}
              <div style={{ 
                position: 'absolute', 
                top: '4px', 
                right: '4px',
                zIndex: 1000
              }}>
                <Button 
                  size="small" 
                  shape="circle" 
                  type="text"
                  onClick={() => toggleComponent(key)}
                  title="Hide component"
                >
                  ✕
                </Button>
              </div>
            </div>
          ));
      }
      
      if (columnCount === 3) {
        // In a 3-column layout, distribute components between columns 2 and 3
        const componentsForThisColumn = componentsToDisplay
          .filter(key => apiData[key] && apiData[key].data) // Only include components with actual data
          .filter((_, index) => {
            // Even indices to column 2, odd indices to column 3
            return columnIndex === 2 ? index % 2 === 0 : index % 2 === 1;
          });
          
        return componentsForThisColumn.map(key => (
          <div key={key} style={{ marginBottom: '16px', position: 'relative', maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
            {memoizedCreateComponent(key, apiData[key], onVersionChange)}
            <div style={{ 
              position: 'absolute', 
              top: '4px', 
              right: '4px',
              zIndex: 1000
            }}>
              <Button 
                size="small" 
                shape="circle" 
                type="text"
                onClick={() => toggleComponent(key)}
                title="Hide component"
              >
                ✕
              </Button>
            </div>
          </div>
        ));
      }
    }
    
    return [];
  };



  return (
    <>
      {/* Header with outcome text and action buttons */}
      {activeAgent && (
        <HeaderActionCenter
          activeAgent={activeAgent}
          componentIds={allComponentIds}
          componentTypes={componentTypes}
          sessionId={sessionId}
          onActionComplete={(action, data) => {
            if (data && data.updatedComponentIds) {
              notifyComponentUpdates(data.updatedComponentIds);
            }
          }}
        />
      )}
      
      {/* Resizable layout */}
      <div className="resizable-container" style={{ 
        display: 'flex', 
        top: 0,
        height: 'auto',
        minHeight: '600px',
        maxHeight: 'calc(100vh - 150px)',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#f0f2f5',
        border: '1px solid #f0f0f0',
        borderRadius: '4px',
        position: 'relative',
        marginBottom: '0',
      }}>
        {/* For 1-column layout, render differently */}
        {columnCount === 1 ? (
          <div style={{ 
            width: '100%', 
            padding: '8px',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 200px)'
          }}>
            {getComponentsForColumn(1)}
          </div>
        ) : (
          <>
            {/* Column 1 - Chat (for 2 and 3 column layouts) */}
            <div 
              style={{ 
                width: `${leftColumnWidth}%`, 
                padding: '8px',
                overflow: 'auto',
                borderRight: '1px solid #e8e8e8',
                position: 'relative',
                height: '100%',
                maxHeight: 'calc(100vh - 200px)' 
              }}
            >
              {chatComponent ? (
                chatComponent
              ) : (
                <div style={{ 
                  padding: '8px', 
                  textAlign: 'center', 
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div>No chat component available</div>
                </div>
              )}
            </div>
            
            {/* Resize handle */}
            <div 
              style={{
                position: 'absolute',
                top: 0,
                left: `${leftColumnWidth}%`,
                width: '8px',
                height: '100%',
                background: '#e1e1e1',
                cursor: 'col-resize',
                zIndex: 10,
                transform: 'translateX(-50%)',
                transition: 'background 0.2s'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                
                const startX = e.clientX;
                const startWidth = leftColumnWidth;
                
                // Style for active dragging
                e.target.style.background = '#6200EE';
                document.body.style.cursor = 'col-resize';
                
                const onMouseMove = (moveEvent) => {
                  const containerWidth = document.querySelector('.resizable-container').offsetWidth;
                  const delta = moveEvent.clientX - startX;
                  const newWidthPx = (startWidth / 100) * containerWidth + delta;
                  const newWidthPercent = (newWidthPx / containerWidth) * 100;
                  
                  handleResize(newWidthPercent);
                };
                
                const onMouseUp = () => {
                  // Reset styles
                  e.target.style.background = '#e1e1e1';
                  document.body.style.cursor = 'default';
                  
                  document.removeEventListener('mousemove', onMouseMove);
                  document.removeEventListener('mouseup', onMouseUp);
                };
                
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#c0c0c0';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#e1e1e1';
              }}
            />
            
            {/* Column 2 */}
            <div 
              style={{ 
                width: `${col2Width}%`, 
                padding: '8px', 
                overflow: 'auto',
                borderRight: columnCount === 3 ? '1px solid #e8e8e8' : 'none',
                maxHeight: 'calc(100vh - 250px)'
              }}
            >
              {getComponentsForColumn(2).length > 0 ? (
                getComponentsForColumn(2)
              ) : (
                <div style={{ 
                  padding: '10px', 
                  textAlign: 'center', 
                  color: '#999',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div>
                    <p>No components in column 2</p>
                    {componentKeys.length > 0 && componentKeys.some(key => !componentDisplay[key]) && (
                      <div>
                        <p>Hidden components:</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                          {componentKeys
                            .filter(key => !componentDisplay[key])
                            .map(key => (
                              <Button 
                                key={key}
                                onClick={() => toggleComponent(key)}
                                size="small"
                                type="primary"
                                ghost
                              >
                                {key}
                              </Button>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Column 3 (only in 3-column layout) */}
            {columnCount === 3 && (
              <>
                {/* Resize handle for column 2/3 (only in 3-column layout) */}
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: `${leftColumnWidth + col2Width}%`,
                    width: '8px',
                    height: '100%',
                    background: '#e1e1e1',
                    cursor: 'col-resize',
                    zIndex: 10,
                    transform: 'translateX(-50%)',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#c0c0c0';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#e1e1e1';
                  }}
                />
                
                <div 
                  style={{ 
                    width: `${col3Width}%`, 
                    padding: '8px', 
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 250px)'
                  }}
                >
                  {getComponentsForColumn(3).length > 0 ? (
                    getComponentsForColumn(3)
                  ) : (
                    <div style={{ 
                      padding: '10px', 
                      textAlign: 'center', 
                      color: '#999',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div>
                        <p>No components in column 3</p>
                        {componentKeys.length > 0 && componentKeys.some(key => !componentDisplay[key]) && (
                          <div>
                            <p>Hidden components:</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                              {componentKeys
                                .filter(key => !componentDisplay[key])
                                .map(key => (
                                  <Button 
                                    key={key}
                                    onClick={() => toggleComponent(key)}
                                    size="small"
                                    type="primary"
                                    ghost
                                  >
                                    {key}
                                  </Button>
                                ))
                              }
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DraggableLayoutManager;